import ruby from './ruby';
import python from './python';
import java from './java';
import php from './php';
import node from './node';
import go from './go';
import dotnet from './dotnet';
import react from './react';
import reactNode from './react-node';
import {packageJson, gitIgnore, webhooksIndex, dotenvExample} from '../next.js';

export const instructions = {
  ruby: {
    build: 'bundle install',
    run: 'ruby server.rb -o 0.0.0.0',
    entry: 'localhost:4242/checkout.html',
  },
  python: {
    build: 'pip3 install -r requirements.txt',
    run: 'export FLASK_APP=server.py\npython3 -m flask run --port=4242',
    entry: 'localhost:4242/checkout.html',
  },
  php: {
    build: 'composer install',
    run: 'php -S 127.0.0.1:4242 -docroot=public',
    entry: 'localhost:4242/public/checkout.html',
  },
  java: {
    build: 'mvn package',
    run:
      'java -cp target/stripe-checkout-1.0.0-SNAPSHOT-jar-with-dependencies.jar com.stripe.sample.Server',
    entry: 'localhost:4242/checkout.html',
  },
  node: {
    build: 'npm install',
    run: 'npm start',
    entry: 'localhost:4242/checkout.html',
  },
  go: {
    build: '',
    run: 'go run server.go',
    entry: 'localhost:4242/checkout.html',
  },
  dotnet: {
    build: 'dotnet restore',
    run: 'dotnet run',
    entry: 'localhost:4242/checkout.html',
  },
  html: {
    build: '',
    run: '',
  },
  react: {
    build: 'npm install',
    run: 'npm start',
    entry: 'localhost:3000/checkout',
  },
};

const buildFiles = {
  php: {
    files: [{name: 'composer.json', raw: php.build}],
  },
  java: {
    files: [{name: 'pom.xml', raw: java}],
  },
  node: {
    files: [{name: 'package.json', raw: node.build}],
  },
  ruby: {
    files: [{name: 'Gemfile', raw: ruby.build}],
  },
  python: {
    files: [{name: 'requirements.txt', raw: python}],
  },
  go: {
    files: [{name: 'go.mod', raw: go.build}],
  },
  dotnet: {
    files: [{name: 'StripeExample.csproj', raw: dotnet.build}],
  },
  react: {
    files: [
      {name: 'package.json', raw: react.build},
      {name: 'src/index.js', raw: react.indexJs},
      {name: 'src/App.css', raw: react.appCss},
      {name: 'public/index.html', raw: react.indexHtml},
    ],
  },
  html: {
    files: [{name: 'public/style.css', raw: react.appCss}],
  },
  reactNode: {
    files: [
      {name: 'package.json', raw: reactNode},
      {name: 'src/index.js', raw: react.indexJs},
      {name: 'src/App.css', raw: react.appCss},
      {name: 'public/index.html', raw: react.indexHtml},
    ],
  },
  next: {
    files: [
      {name: 'package.json', raw: packageJson},
      {name: '.gitignore', raw: gitIgnore},
      {name: '.env.example', raw: dotenvExample},
    ],
  },
  nextServer: {
    files: [{name: 'pages/api/webhooks.js', raw: webhooksIndex}],
  },
};

const generateFiles = (client, server) => {
  if (client === 'react' && server === 'node') {
    return buildFiles.reactNode;
  } else {
    return {files: [...buildFiles[client].files, ...buildFiles[server].files]};
  }
};

const nodeReactReadme =
  '1. Build the application\n\n```npm install```\n\n 2. Run the application\n\n```npm start```\n\n3. Go to [localhost:4242/checkout.html](localhost:4242/checkout.html/)\n';

const nextReadme = `
### Development
1. Build the application
\`\`\`shell
$ npm install
\`\`\`

2. _Optional_: download and run the [Stripe CLI](https://stripe.com/docs/stripe-cli)
\`\`\`shell
$ stripe listen --forward-to localhost:3000/api/webhooks
\`\`\`

3. Run the application
\`\`\`shell
$ STRIPE_WEBHOOK_SECRET=$(stripe listen --print-secret) npm run dev
\`\`\`

4. Go to [localhost:3000](http://localhost:3000)

### Production
1. Build the application
\`\`\`shell
$ npm install

$ npm build
\`\`\`

2. Run the application
\`\`\`shell
$ npm start
\`\`\`
`;

const readme = (client, server) => {
  let doc = `# Accept a Card Payment\n\nStripe Checkout is the fastest way to get started with payments. Included are some basic build and run scripts you can use to start up the application.\n\n## Running the sample\n\n`;
  const serverInstructions = instructions[server];
  const clientInstructions = instructions[client];
  if (client === 'react' && server === 'node') {
    return doc + nodeReactReadme;
  }
  if (client === 'next') {
    return nextReadme;
  }
  let step = 1;
  if (serverInstructions.build) {
    doc += `${step}. Build the server\n\n\`\`\`\n${serverInstructions.build}\n\`\`\`\n\n`;
    step++;
  }
  if (serverInstructions.run) {
    doc += `${step}. Run the server\n\n\`\`\`\n${serverInstructions.run}\n\`\`\`\n\n`;
    step++;
  }
  if (clientInstructions.build) {
    doc += `${step}. Build the client app\n\n\`\`\`\n${clientInstructions.build}\n\`\`\`\n\n`;
    step++;
  }
  if (clientInstructions.run) {
    doc += `${step}. Run the client app\n\n\`\`\`\n${clientInstructions.run}\n\`\`\`\n\n`;
    step++;
  }
  doc += `${step}. Go to [http://${clientInstructions.entry ||
    serverInstructions.entry}](http://${clientInstructions.entry ||
    serverInstructions.entry})\n`;

  return doc;
};

export default {
  readme,
  buildInstructions: generateFiles,
};
